import React, { useState, useEffect } from 'react';
import { Package, Plus, TrendingUp, AlertTriangle, DollarSign, Edit, History, Search, FileSpreadsheet } from 'lucide-react';
import StockDetailsModal from '../Stocks/StockDetailsModal';
import { API_URL } from '../../services/api';

const StockMatieres = ({ mode = 'or' }) => {
  // mode: 'or' → METAL (or/argent), 'pierres' → PIERRE (diamants/gemmes)
  const categorie = mode === 'pierres' ? 'PIERRE' : 'METAL';
  const [stock, setStock] = useState([]);
  const [prixActuels, setPrixActuels] = useState({ or: 0, chaine: 0, mousqueton: 0 });
  const [prixMarche, setPrixMarche] = useState(null);
  const [prixMoyens, setPrixMoyens] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    reference: '',
    categorie: 'METAL',
    type_matiere: '',
    quantite: '',
    quantite_minimum: 0,
    unite: 'g',
    fournisseur_nom: '',
    prix_achat_unitaire: '',
    date_achat: new Date().toISOString().split('T')[0],
    numero_facture: '',
    purete: '',
    couleur: '',
    emplacement: '',
    notes: ''
  });

  useEffect(() => {
    loadStock();
    if (mode !== 'pierres') {
      loadPrixActuels();
      loadPrixMarche();
    }
    loadPrixMoyens();
    loadStats();
  }, [mode]);

  const loadPrixMarche = async () => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/prix/marche`);
      const data = await response.json();
      if (!data.error) {
        setPrixMarche(data);
      } else {
        setPrixMarche(data); // still set it so we can show "indisponible"
      }
    } catch (error) {
      console.error('Erreur chargement prix marché:', error);
      setPrixMarche(null);
    }
  };

  const loadStock = async () => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/stock?categorie=${categorie}`);
      const data = await response.json();
      setStock(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement stock:', error);
      setStock([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPrixActuels = async () => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/prix/actuel`);
      if (!response.ok) {
        throw new Error('Erreur API');
      }
      const data = await response.json();
      // S'assurer que toutes les valeurs sont des nombres
      setPrixActuels({
        or: parseFloat(data?.or) || 0,
        chaine: parseFloat(data?.chaine) || 0,
        mousqueton: parseFloat(data?.mousqueton) || 0,
        argent: parseFloat(data?.argent) || 0
      });
    } catch (error) {
      console.error('Erreur chargement prix:', error);
      setPrixActuels({ or: 0, chaine: 0, mousqueton: 0, argent: 0 });
    }
  };

  const loadPrixMoyens = async () => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/prix-moyens`);
      const data = await response.json();
      setPrixMoyens(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement prix moyens:', error);
      setPrixMoyens([]);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/stock-matieres/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Stock ajouté avec succès');
        setShowAddModal(false);
        resetForm();
        loadStock();
        loadStats();
      }
    } catch (error) {
      console.error('Erreur ajout stock:', error);
      alert('Erreur lors de l\'ajout du stock');
    }
  };

  const resetForm = () => {
    setFormData({
      reference: '',
      categorie: 'METAL',
      type_matiere: '',
      quantite: '',
      quantite_minimum: 0,
      unite: 'g',
      fournisseur_nom: '',
      prix_achat_unitaire: '',
      date_achat: new Date().toISOString().split('T')[0],
      numero_facture: '',
      purete: '',
      couleur: '',
      emplacement: '',
      notes: ''
    });
  };

  const viewDetails = async (stockId) => {
    try {
      const response = await fetch(`${API_URL}/stock-matieres/stock/${stockId}`);
      const data = await response.json();
      setSelectedStock(data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
    }
  };

  const getNiveauStockColor = (niveau) => {
    switch (niveau) {
      case 'ALERTE': return 'text-red-600 bg-red-50';
      case 'BAS': return 'text-orange-600 bg-orange-50';
      default: return 'text-green-600 bg-green-50';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Chargement...</div>;
  }

  // Types qui vont dans l'onglet Or (karatage explicite + métaux précieux)
  const isOrType = (t) => /^OR\s+\d+K$/i.test(t?.trim()) || /^ARGENT$/i.test(t) || /^PLATINE$/i.test(t);
  // Types qui vont dans l'onglet Pierres (tout ce qui n'est pas métal connu)
  const METAL_TYPES = ['OR', 'CHAINE', 'MOUSQUETON', 'ARGENT', 'PLATINE'];
  const isPierreType = (t) => !isOrType(t) && !METAL_TYPES.includes(t?.toUpperCase()?.trim());

  const filteredPrixMoyens = prixMoyens.filter(pm =>
    mode === 'or' ? isOrType(pm.type_matiere) : isPierreType(pm.type_matiere)
  );

  // Filtrer le tableau de stock selon le mode + recherche
  const filteredStock = stock.filter(item => {
    const modeOk = mode === 'or' ? isOrType(item.type_matiere) : isPierreType(item.type_matiere);
    if (!modeOk) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (item.reference || '').toLowerCase().includes(term) ||
      (item.type_matiere || '').toLowerCase().includes(term) ||
      (item.fournisseur_nom || '').toLowerCase().includes(term) ||
      (item.purete || '').toLowerCase().includes(term) ||
      (item.couleur || '').toLowerCase().includes(term) ||
      (item.emplacement || '').toLowerCase().includes(term) ||
      (item.notes || '').toLowerCase().includes(term)
    );
  });

  const titre = mode === 'pierres' ? 'Stock Pierres & Diamants' : 'Stock Or & Métaux';
  const btnLabel = mode === 'pierres' ? 'Acheter Pierres' : 'Acheter Or / Métal';

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{titre}</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Chercher réf, type, fournisseur..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:outline-none"
            />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full sm:w-auto px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center space-x-2"
          >
            <Plus size={20} />
            <span>{btnLabel}</span>
          </button>
          {/* §98 - Export Excel */}
          <button
            onClick={async () => {
              try {
                const endpoint = mode === 'pierres' ? 'stock-pierres-v2' : 'stock-or';
                const res = await fetch(`${API_URL}/exports/${endpoint}`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
                });
                if (!res.ok) throw new Error('Export failed: ' + res.status);
                const blob = await res.blob();
                const cd = res.headers.get('Content-Disposition') || '';
                const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/);
                const fname = m ? decodeURIComponent(m[1]) : `Stock_${mode}.xlsx`;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = fname;
                document.body.appendChild(a); a.click();
                a.remove(); window.URL.revokeObjectURL(url);
              } catch (e) {
                alert('Erreur export Excel : ' + e.message);
              }
            }}
            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
            title="Télécharger Excel"
          >
            <FileSpreadsheet size={18} />
            <span>Exporter Excel</span>
          </button>
        </div>
      </div>

      {/* Bilan Or complet — seulement en mode 'or' */}
      {mode === 'or' && (() => {
        const prixActuel = parseFloat(prixActuels.or) || 0;
        // Calculer stock total or et valeur d'achat depuis le stock chargé
        const orItems = filteredStock.filter(s => (s.type_matiere || '').toUpperCase().includes('OR'));
        const stockTotalOr = orItems.reduce((s, i) => s + parseFloat(i.quantite_disponible || 0), 0);
        // Chercher le prix moyen pondéré
        const pmOr = prixMoyens.find(p => p.type_matiere === 'OR 18K');
        const prixMoyenAchat = pmOr ? parseFloat(pmOr.prix_moyen_pondere) : 0;
        // Valeurs
        const valeurActuelle = stockTotalOr * prixActuel;
        const valeurAchat = stockTotalOr * prixMoyenAchat;
        const plusValue = valeurActuelle - valeurAchat;
        const plusValuePct = valeurAchat > 0 ? ((plusValue / valeurAchat) * 100) : 0;

        return (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Prix actuel */}
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl p-4 text-white shadow-lg">
              <p className="text-xs opacity-80 uppercase tracking-wide">Prix or actuel (défini)</p>
              <p className="text-3xl font-bold mt-1">{prixActuel.toFixed(2)}</p>
              <p className="text-sm opacity-75">DH / gramme</p>
            </div>

            {/* Prix moyen d'achat */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-4 text-white shadow-lg">
              <p className="text-xs opacity-80 uppercase tracking-wide">Prix moyen d'achat</p>
              <p className="text-3xl font-bold mt-1">{prixMoyenAchat > 0 ? prixMoyenAchat.toFixed(2) : '—'}</p>
              <p className="text-sm opacity-75">DH / gramme</p>
              {prixMoyenAchat > 0 && prixActuel > 0 && (
                <p className="text-xs mt-1 opacity-90">
                  {prixActuel > prixMoyenAchat ? '📈' : '📉'} Écart : {(prixActuel - prixMoyenAchat).toFixed(2)} DH/g
                </p>
              )}
            </div>

            {/* Stock total or */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
              <p className="text-xs opacity-80 uppercase tracking-wide">Stock total or</p>
              <p className="text-3xl font-bold mt-1">{stockTotalOr.toFixed(1)}g</p>
              <div className="text-sm opacity-75 mt-1 space-y-0.5">
                <p>Au prix actuel : {valeurActuelle.toLocaleString('fr-MA', {maximumFractionDigits: 0})} DH</p>
                {prixMoyenAchat > 0 && (
                  <p>Au prix d'achat : {valeurAchat.toLocaleString('fr-MA', {maximumFractionDigits: 0})} DH</p>
                )}
              </div>
            </div>

            {/* Plus-value */}
            <div className={`bg-gradient-to-br ${plusValue >= 0 ? 'from-green-500 to-emerald-700' : 'from-red-500 to-red-700'} rounded-xl p-4 text-white shadow-lg`}>
              <p className="text-xs opacity-80 uppercase tracking-wide">Plus-value potentielle</p>
              <p className="text-3xl font-bold mt-1">
                {plusValue >= 0 ? '+' : ''}{plusValue.toLocaleString('fr-MA', {maximumFractionDigits: 0})} DH
              </p>
              {prixMoyenAchat > 0 && (
                <p className="text-sm opacity-75 mt-1">
                  {plusValuePct >= 0 ? '+' : ''}{plusValuePct.toFixed(1)}% depuis l'achat
                </p>
              )}
              <a
                href="https://www.goldpricedata.com/fr/gold-rates/morocco/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition"
              >
                📊 Cours de l'or en direct
              </a>
            </div>
          </div>

          {/* Bandeau prix du marché */}
          {prixMarche && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  {prixMarche['18k'] ? (() => {
                    const marche18k = parseFloat(prixMarche['18k']);
                    const diff = prixActuel - marche18k;
                    const diffAbs = Math.abs(diff);
                    return (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase">18K March&#233;</span>
                          <span className="text-lg font-bold text-gray-900">
                            {marche18k.toLocaleString('fr-MA', {maximumFractionDigits: 0})} DH/g
                          </span>
                        </div>
                        <div className="text-gray-400">|</div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase">Vous</span>
                          <span className="text-lg font-bold text-gray-900">
                            {prixActuel.toLocaleString('fr-MA', {maximumFractionDigits: 0})} DH/g
                          </span>
                        </div>
                        <div className="text-gray-400">|</div>
                        <span className={`text-sm font-bold ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {diff >= 0 ? '+' : '-'}{diffAbs.toLocaleString('fr-MA', {maximumFractionDigits: 0})} DH/g {diff >= 0 ? 'au-dessus' : 'en-dessous'}
                        </span>
                      </>
                    );
                  })() : (
                    <span className="text-sm text-gray-500">Prix march&#233; indisponible</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  {prixMarche['24k'] && (
                    <span>24K: {parseFloat(prixMarche['24k']).toLocaleString('fr-MA', {maximumFractionDigits: 0})} DH/g</span>
                  )}
                  <span>Source: {prixMarche.source || 'goldpricedata.com'}</span>
                  {prixMarche.cached && <span>(cache)</span>}
                  <button
                    onClick={loadPrixMarche}
                    className="text-orange-500 hover:text-orange-700 underline"
                  >
                    Actualiser
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
        );
      })()}

      {/* Tableau des prix moyens - CLIQUABLE - filtré selon le mode */}
      {filteredPrixMoyens.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="text-lg font-semibold mb-3 flex items-center">
            <DollarSign className="mr-2" />
            Prix moyens d'achat par type de matière
          </h3>
          <div className="overflow-x-auto pb-1">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 min-w-[320px]">
            {filteredPrixMoyens.map((pm) => (
              <div
                key={pm.type_matiere}
                onClick={() => setSelectedType(pm.type_matiere)}
                className="border rounded-lg p-3 cursor-pointer hover:shadow-lg hover:border-orange-500 transition-all transform hover:scale-105"
              >
                <p className="text-sm font-medium text-gray-700">{pm.type_matiere}</p>
                <p className="text-lg font-bold text-blue-600">
                  {parseFloat(pm.prix_moyen_pondere).toFixed(2)} DH/{pm.unite}
                </p>
                <div className="text-xs text-gray-500 mt-1">
                  <p>Min: {parseFloat(pm.prix_min).toFixed(2)} DH</p>
                  <p>Max: {parseFloat(pm.prix_max).toFixed(2)} DH</p>
                  <p className="font-medium mt-1">{pm.nombre_achats} achat(s)</p>
                </div>
                <p className="text-xs text-orange-600 font-semibold mt-2">👁️ Cliquer pour détails</p>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

      {/* Modale de détails par type */}
      {selectedType && (
        <StockDetailsModal
          type={selectedType}
          onClose={() => setSelectedType(null)}
        />
      )}

      {/* Statistiques */}
      {stats && stats.alertes_stock_bas > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle className="text-red-500 mr-3" size={24} />
            <p className="text-red-700 font-medium">
              {stats.alertes_stock_bas} lot(s) en alerte stock bas !
            </p>
          </div>
        </div>
      )}

      {/* Mobile cards — visible uniquement sur petits écrans */}
      <div className="md:hidden space-y-3 mb-4">
        {filteredStock.filter(item => item && item.id).map((item) => (
          <div key={item.id} className={`bg-white rounded-lg shadow p-4 border-l-4 ${item.niveau_stock === 'ALERTE' ? 'border-red-500' : item.niveau_stock === 'BAS' ? 'border-orange-400' : 'border-green-400'}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-bold text-gray-900 flex items-center gap-1">
                  <Package size={14} className="text-gray-400" />
                  {item.reference || '-'}
                </div>
                <div className="text-xs text-gray-500">{item.type_matiere || '-'} · {item.categorie || '-'}</div>
              </div>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getNiveauStockColor(item.niveau_stock || 'normal')}`}>
                {item.niveau_stock || 'normal'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <div className="text-xs text-gray-500">Disponible</div>
                <div className="font-bold text-gray-900">{parseFloat(item.quantite_disponible || 0).toFixed(3)} {item.unite === 'carat' ? 'ct' : (item.unite || 'g')}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Prix/unité</div>
                <div className="font-bold text-gray-900">{parseFloat(item.prix_achat_unitaire || 0).toFixed(2)} DH</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Valeur stock</div>
                <div className="font-medium text-gray-700">{parseFloat(item.valeur_stock || 0).toFixed(2)} DH</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Fournisseur</div>
                <div className="font-medium text-gray-700 truncate">{item.fournisseur_nom || '-'}</div>
              </div>
            </div>
            <button
              onClick={() => viewDetails(item.id)}
              className="w-full py-2.5 bg-orange-600 text-white rounded-lg text-sm font-semibold"
            >
              Voir historique
            </button>
          </div>
        ))}
      </div>

      {/* Liste du stock — tableau desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poids/Quantité</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix/unité</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valeur</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Niveau</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredStock.filter(item => item && item.id).map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Package className="text-gray-400 mr-2" size={16} />
                    <span className="font-medium text-gray-900">{item.reference || '-'}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{item.type_matiere || '-'}</div>
                    <div className="text-xs text-gray-500">{item.categorie || '-'}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">
                    {parseFloat(item.quantite_disponible || 0).toFixed(3)} {item.unite === 'carat' ? 'ct' : (item.unite || 'g')}
                  </div>
                  <div className="text-xs text-gray-500">
                    sur {parseFloat(item.quantite_initiale || 0).toFixed(3)} {item.unite === 'carat' ? 'ct' : (item.unite || 'g')}
                  </div>
                  {item.unite === 'carat' && (
                    <div className="text-xs font-semibold text-blue-600 mt-1">
                      💎 {parseFloat(item.quantite_disponible || 0).toFixed(3)} carats
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {parseFloat(item.prix_achat_unitaire || 0).toFixed(2)} DH
                  </div>
                  <div className="text-xs text-gray-500">
                    par {item.unite === 'carat' ? 'carat' : (item.unite || 'g')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm font-medium text-gray-900">
                    <DollarSign size={14} className="mr-1" />
                    {parseFloat(item.valeur_stock || 0).toFixed(2)} DH
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getNiveauStockColor(item.niveau_stock || 'normal')}`}>
                    {item.niveau_stock || 'normal'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.fournisseur_nom || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => viewDetails(item.id)}
                    className="text-orange-600 hover:text-orange-900 mr-3"
                  >
                    <History size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Ajouter Stock */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Acheter Matières Premières</h3>
            <form onSubmit={handleAddStock}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Référence *</label>
                  <input
                    type="text"
                    required
                    value={formData.reference}
                    onChange={(e) => setFormData({...formData, reference: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <select
                    value={formData.categorie}
                    onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="METAL">METAL</option>
                    <option value="PIERRE">PIERRE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    required
                    value={formData.type_matiere}
                    onChange={(e) => setFormData({...formData, type_matiere: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="OR 18K">OR 18K</option>
                    <option value="ARGENT">ARGENT</option>
                    <option value="CHAINE">CHAINE</option>
                    <option value="MOUSQUETON">MOUSQUETON</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.001"
                    required
                    value={formData.quantite}
                    onChange={(e) => setFormData({...formData, quantite: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unité</label>
                  <select
                    value={formData.unite}
                    onChange={(e) => setFormData({...formData, unite: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="g">Grammes (g)</option>
                    <option value="ct">Carats (ct)</option>
                    <option value="piece">Pièce</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prix/unité *</label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    required
                    value={formData.prix_achat_unitaire}
                    onChange={(e) => setFormData({...formData, prix_achat_unitaire: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="DH"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                  <input
                    type="text"
                    value={formData.fournisseur_nom}
                    onChange={(e) => setFormData({...formData, fournisseur_nom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date d'achat</label>
                  <input
                    type="date"
                    value={formData.date_achat}
                    onChange={(e) => setFormData({...formData, date_achat: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">N° Facture</label>
                  <input
                    type="text"
                    value={formData.numero_facture}
                    onChange={(e) => setFormData({...formData, numero_facture: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emplacement</label>
                  <input
                    type="text"
                    value={formData.emplacement}
                    onChange={(e) => setFormData({...formData, emplacement: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Coffre, Tiroir..."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Ajouter au Stock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détails */}
      {showDetailModal && selectedStock && selectedStock.stock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Détails du Stock</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">Référence</p>
                <p className="font-medium">{selectedStock.stock?.reference || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-medium">{selectedStock.stock?.type_matiere || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Quantité disponible</p>
                <p className="font-medium">{parseFloat(selectedStock.stock?.quantite_disponible || 0).toFixed(3)} {selectedStock.stock?.unite || 'g'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Prix d'achat</p>
                <p className="font-medium">{parseFloat(selectedStock.stock?.prix_achat_unitaire || 0).toFixed(2)} DH/{selectedStock.stock?.unite || 'g'}</p>
              </div>
            </div>
            
            <h4 className="font-bold mb-3">Historique des mouvements</h4>
            <div className="space-y-2">
              {selectedStock.mouvements.map((mvt, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <div>
                    <span className={`px-2 py-1 text-xs rounded ${mvt.type_mouvement === 'ENTREE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {mvt.type_mouvement}
                    </span>
                    <span className="ml-2 text-sm">{mvt.motif}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{parseFloat(mvt.quantite).toFixed(3)} {selectedStock.stock.unite}</p>
                    <p className="text-xs text-gray-500">{new Date(mvt.date_mouvement).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockMatieres;
